import { Farm } from '@abaco/farm-registry/src/modules/farm-registry/models/Farm';
import { HTTPClient } from '@abaco/web-common/src/HTTP';
import { PagedResults, PagingParams } from '@abaco/web-common/src/Paging';
import { Agreement, AgreementType, AgreementFilter, NewAgreementOut, PartyType, Party, EditAgreementMainDataOut, FormSection, FormAttachments, EditPartiesOut, PartyIn, IndicatorItem } from '../models/NewAgreementsModel';

export default class NewAgreementsApi {
    private http: HTTPClient;
    private rootPath: string;

    constructor(http: HTTPClient, rootPath: string) {
        this.http = http;
        this.rootPath = rootPath;
    }

    async getAgreements(farm: Farm, filter: AgreementFilter | null,  paging: PagingParams, order?: string | null): Promise<PagedResults<Agreement>> {
        const filterArr = [];
        if (filter) {
            if (filter.id) filterArr.push(`agreement_id eq ${filter.id}`);
            if (filter.status) filterArr.push(`ds_agreement_status like ${filter.status.toUpperCase()}`);
            if (filter.type) filterArr.push(`ds_agreement_type like ${filter.type.toUpperCase()}`);
        }
        let sortStr: string | null = null;
        if (order) {
            sortStr = order;
        }
        const res = await this.http.get(`${this.rootPath}/agreements/agreements`, {
            params: {
                count: paging.count,
                offset: paging.offset,
                filter: filterArr.length > 0 ? filterArr : undefined,
                sort: sortStr ? sortStr : undefined,
                subject_id: farm.pk_cuaa,

            }
        });
        return res.data;

    }

    async getAgreementType(): Promise<AgreementType[]> {
        const res = await this.http.get(`${this.rootPath}/agreements/dictionary/agreement_types`, {
            params: {
                count: 300,
                offset: 0,
                filter: "fg_active eq 1",
            }
        });
        return res.data.records;
    }

    async getAgreementPartiesType(): Promise<PartyType[]> {
        const res = await this.http.get(`${this.rootPath}/agreements/dictionary/agreement_party_types`, {
            params: {
                count: 300,
                offset: 0,
            }
        });
        return res.data.records;
    }

    async getDefaultParties(pk_cuaa: number, agreement_type_id: number): Promise<PartyType[]> {
        const res = await this.http.get(`${this.rootPath}/agreements/dictionary/agreement_types/${agreement_type_id}/parties_default/${pk_cuaa}`);
        return res.data.records;
    }

    async getAgreementPartiesForEdit(agreement_id: number): Promise<PartyIn[]> {
        const res = await this.http.get(`${this.rootPath}/agreements/agreements/${agreement_id}/parties`);
        const defaultPartiestoEdit: PartyIn[] = res.data.records?.map((r: PartyIn) => ({
            company_code: r.company_code,
            subject_id: r.subject_id,
            full_name: r.full_name,
            sco_party_type: r.sco_party_type,
            de_party_type: r.de_party_type,
            cod_party_type: r.cod_party_type,
            ds_party_type: r.ds_party_type
        })) || [];

        return defaultPartiestoEdit;
    }

    async getAgreementById(contract_id: number): Promise<Agreement[]> {
        const res = await this.http.get(`${this.rootPath}/agreements/agreements`, {
            params: {
                count: 1,
                offset: 0,
                filter: `agreement_id eq ${contract_id}`,
            }
        });
        return res.data.records;
    }

    async saveAgreement(agreement: NewAgreementOut): Promise<Agreement> {
        const res = await this.http.post(`${this.rootPath}/agreements/agreements`, agreement);
        return res.data;
    }

    async getAgreementIndicators(farm: Farm, agreement_id: number): Promise<IndicatorItem[]> {
        const res = await this.http.get(`${this.rootPath}/agreements/agreements/${agreement_id}/indicators`);
        return res.data;
    }

    //API call that allows you to retrieve the parties
    async getParties(pagingParams: PagingParams, company_code: string | null = null, fiscal_code: string | null = null, vat_number: string | null = null, name: string | null = null ): Promise<PagedResults<Party>>{
        const filter: string[] = [];

        if(company_code){
            filter.push(`company_code like ${company_code.toUpperCase()}`);
        }
        if(fiscal_code){
            filter.push(`fiscal_code like ${fiscal_code.toUpperCase()}`);
        }
        if(vat_number){
            filter.push(`vat_number like ${vat_number.toUpperCase()}`);
        }
        if(name){
            filter.push(`full_name like ${name.toUpperCase()}`);
        }

        const res = await this.http.get(`${this.rootPath}/people_registry/subjects`, {
            params: {
                filter: filter.length ? filter : undefined,
                offset: pagingParams.offset,
                count: pagingParams.count,
            }
        });
        return res.data;
    }

    async editAgreementMainData(agreement: EditAgreementMainDataOut, agreement_id: number): Promise<Agreement> {
        const res = await this.http.put(`${this.rootPath}/agreements/agreements/${agreement_id}`, agreement);
        return res.data;
    }

    async editAgreementParties(agreement: EditPartiesOut, agreement_id: number): Promise<PagedResults<PartyIn>> {
        const res = await this.http.put(`${this.rootPath}/agreements/agreements/${agreement_id}/parties`, agreement);
        return res.data;
    }


    async getFormsSection(agreement_id: number): Promise<FormSection> {
        const res = await this.http.get(`${this.rootPath}/agreements/agreements/${agreement_id}/forms`);
        const formSection = res.data;
        return formSection;
    }


    async getFormsAttachments(agreement_id: number): Promise<FormAttachments[]> {
        const res = await this.http.get(`${this.rootPath}/agreements/agreements/${agreement_id}/attachments`);
        return res.data;
    }

    async getAgreementByCode(contract_code: string, subject_id: string | null = null): Promise<Agreement[]> {

        const filters: string[] = [];
        if(contract_code){
            filters.push(`agreement_code eq ${contract_code}`)
        }

        const res = await this.http.get(`${this.rootPath}/agreements/agreements`, {
            params: {
                subject_id: subject_id,
                count: 1,
                offset: 0,
                filter: filters,
            }
        });
        return res.data.records;
    }


    async getPermit(ssoFunction: string): Promise<boolean> {
        const res = await this.http.get(`/sitiagri-rest-api/api_basic/v1/user_registry/sso_functions/DOMANDE/${ssoFunction}`);
        return res.data.result;
    }


}
